import React, { useMemo, useState, useEffect, useRef } from "react";
import ScheduleDetailModal from "../components/ScheduleDetailModal"; // [NEW]
import {
    X, Sun,
    ArrowDownRight,
} from "lucide-react";
import { triggerHappyMoment } from "../utils/confetti";
import { appId } from "../firebase";
import { summarizeAttendance, summarizeFinance, summarizeSchedule, summarizeVisitLogs } from "../utils";
import { DEFAULT_THEME } from "../constants/theme";
import { Responsive, WidthProvider } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { useModal } from "../contexts/ModalContext";
import { useDashboardPreferences, useDeptGoals } from "../hooks/useUserPreferences"; // [NEW] Firestore 동기화
import { DASHBOARD_DEPT_INFO, DASHBOARD_ICONS, DASHBOARD_SKY_THEMES } from "./dashboard/dashboardConstants";
import DashboardBannerSection from "./dashboard/DashboardBannerSection";
import DashboardKpiSection from "./dashboard/DashboardKpiSection";
import DashboardActivitySection from "./dashboard/DashboardActivitySection";
import DashboardRecommendationSection from "./dashboard/DashboardRecommendationSection";
import DashboardScheduleSection from "./dashboard/DashboardScheduleSection";
import DashboardTodoSection from "./dashboard/DashboardTodoSection";
import DashboardTeacherGoalsCard from "./dashboard/DashboardTeacherGoalsCard";
import DashboardGlassCard from "./dashboard/DashboardGlassCard";
import DashboardCardBase from "./dashboard/DashboardCard";
import DashboardMessageModal from "./dashboard/DashboardMessageModal";
import DashboardScheduleEditModal from "./dashboard/DashboardScheduleEditModal";
import DashboardRecommendationModal from "./dashboard/DashboardRecommendationModal";
import DashboardRadarConfirmModal from "./dashboard/DashboardRadarConfirmModal";
import DashboardSettingsModal from "./dashboard/DashboardSettingsModal";
import useDashboardDerivedData from "./dashboard/useDashboardDerivedData";
import useDashboardUiState from "./dashboard/useDashboardUiState";
import useLongPress from "./dashboard/useLongPress";
import {
    getCurrentSkyTheme,
    getDailyVerse,
    getGreetingInfo,
    getScheduleDDay,
    getScheduleTypeColor,
    getScheduleTypeName,
} from "./dashboard/dashboardDerivedUtils";

const ResponsiveGridLayout = WidthProvider(Responsive);

const DashboardCard = (props) => (
    <DashboardCardBase
        GlassCardComponent={DashboardGlassCard}
        birthdayFallbackIcon={DASHBOARD_ICONS.birthday}
        {...props}
    />
);

// [OPTIMIZED] 🚀 Firestore 중복 조회 최적화 완료
// DashboardView는 이제 useData에서 받은 attendanceLogs를 직접 사용합니다.
// 결과: Firestore 읽기 횟수 대폭 감소!


export default function DashboardView({ students = [], teachers = [], attendanceLogs: initialAttendanceLogs = [], schedules = [], finances = [], annualBudget, onNavigate, userProfile, onAdd, onUpdate, onDelete, meetingMinutes = [], visitLogs = [], isSettingsOpen, onCloseSettings, onToggleSettings, isAaronOpen, selectedDepartment, theme = DEFAULT_THEME, chatHistory = [], setChatHistory = () => { }, onOpenAaronWidget, earnedBadges = [], level = 1, levelProgress = 0, totalPoints = 0, onOpenProfile, onNavigateToStatisticsSection, onOpenStudentDetailFromRecommendation }) {

    // [OPTIMIZED] 🚀 Firestore 최적화: useData에서 받은 attendanceLogs를 직접 사용
    // 기존: 별도로 Firestore에서 다시 조회 (중복 읽기 발생)
    // 변경: props로 받은 데이터를 그대로 사용하여 읽기 횟수 대폭 감소
    const attendanceLogs = useMemo(() => {
        // props가 변경될 때만 업데이트
        return initialAttendanceLogs || [];
    }, [initialAttendanceLogs]);
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedSchedule, setSelectedSchedule] = useState(null);

    const [selectedRecommendation, setSelectedRecommendation] = useState(null); // New state
    // [NEW] Radar Dismissal Logic (Daily Persistence)
    const [dismissedRadarIds, setDismissedRadarIds] = useState(() => {
        const today = new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD
        const saved = localStorage.getItem(`dashboard_radar_dismissed_${today}`);
        return saved ? JSON.parse(saved) : [];
    });
    const [radarConfirmData, setRadarConfirmData] = useState(null);

    const handleAaronSubmit = () => { }; // [REMOVED] Aaron widget removed

    const { openConfirm, openAlert } = useModal();

    // [NEW] Firestore 동기화된 환경설정 훅
    const { prefs: dashboardPrefs, updatePrefs: updateDashboardPrefs, updatePrefsBatch: updateDashboardPrefsBatch } = useDashboardPreferences();

    const {
        bannerMessage,
        setBannerMessage,
        isMessageModalOpen,
        setIsMessageModalOpen,
        messageForm,
        setMessageForm,
        visibleWidgets,
        toggleWidget,
        layouts,
        isEditMode,
        setIsEditMode,
        onLayoutChange,
        currentBreakpoint,
        onBreakpointChange,
        getWidgetHeight,
        deleteWidget,
        resetLayout,
        isDragging,
        setIsDragging,
        isMobile,
    } = useDashboardUiState({
        dashboardPrefs,
        updateDashboardPrefs,
        updateDashboardPrefsBatch,
        openConfirm,
        theme,
    });

    const {
        filteredStudents,
        filteredSchedules,
        filteredFinances,
        totalStudents,
        birthdayStudents,
        remainingBudget,
        avgAttendance,
        attStatus,
        budgetStatus,
        birthStatus,
        radarRecommendations,
    } = useDashboardDerivedData({
        students,
        teachers,
        schedules,
        finances,
        attendanceLogs,
        visitLogs,
        selectedDepartment,
        annualBudget,
        dismissedRadarIds,
        icons: DASHBOARD_ICONS,
    });

    const handleDetailChecklistToggle = (schedule, itemId) => {
        const updatedChecklists = (schedule.checklists || []).map(item =>
            item.id === itemId ? { ...item, checked: !item.checked } : item
        );
        const updatedSchedule = { ...schedule, checklists: updatedChecklists };
        onUpdate(updatedSchedule);
        if (selectedSchedule && selectedSchedule.id === schedule.id) setSelectedSchedule(updatedSchedule);
    };


    // [MODIFIED] Department Goals - 공유 훅으로 대시보드+통계 동기화
    const { deptGoals: sharedDeptGoals, updateDeptGoals } = useDeptGoals();

    // 로컬 상태는 기본값과 공유 데이터를 병합하여 사용
    const deptInfo = useMemo(() => {
        return { ...DASHBOARD_DEPT_INFO, ...sharedDeptGoals };
    }, [sharedDeptGoals]);

    const handleGoalChange = (dept, field, value, idx = null) => {
        // [FIX] DASHBOARD_DEPT_INFO의 기본값과 현재 상태를 병합하여 goals가 사라지지 않도록 함
        const baseDept = DASHBOARD_DEPT_INFO[dept] || { slogan: "", goals: ["", "", ""] };
        const currentDept = deptInfo[dept] || {};
        const newDept = {
            slogan: currentDept.slogan ?? baseDept.slogan,
            goals: [...(currentDept.goals?.length ? currentDept.goals : baseDept.goals)]
        };

        if (idx !== null) {
            // goals 배열의 특정 인덱스 수정
            newDept.goals[idx] = value;
        } else {
            // slogan 등 다른 필드 수정
            newDept[field] = value;
        }

        // 공유 훅으로 업데이트 (대시보드 + 통계 모두 동기화)
        updateDeptGoals({ ...sharedDeptGoals, [dept]: newDept });
    };

    const currentSlogan = deptInfo[selectedDepartment]?.slogan || "주님의 사랑으로 성장하는 공동체";
    const currentGoals = deptInfo[selectedDepartment]?.goals || ["예배 회복", "공동체 사랑", "제자 양육"];

    // [NEW] 배너 메시지 드래그 로직
    const bannerRef = useRef(null);
    // [NEW] Drag State for Swapping
    const dragRef = useRef(null);
    const isSwapping = useRef(false);
    const [isDraggingMessage, setIsDraggingMessage] = useState(false);
    const dragOffset = useRef({ x: 0, y: 0 });
    const messageDims = useRef({ w: 0, h: 0 }); // [NEW] Store message dimensions
    const requestRef = useRef(); // [NEW] For RAF

    const handleMessageMouseDown = (e) => {
        if (!isEditMode) return;
        e.stopPropagation();
        e.preventDefault();
        const bannerRect = bannerRef.current.getBoundingClientRect();
        const messageRect = e.currentTarget.getBoundingClientRect();
        dragOffset.current = {
            x: e.clientX - messageRect.left,
            y: e.clientY - messageRect.top
        };
        messageDims.current = { w: messageRect.width, h: messageRect.height }; // [NEW] Capture size
        setIsDraggingMessage(true);
    };

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isDraggingMessage || !bannerRef.current) return;

            // [NEW] RAF for performance
            cancelAnimationFrame(requestRef.current);
            requestRef.current = requestAnimationFrame(() => {
                const bannerRect = bannerRef.current.getBoundingClientRect();

                let newX = e.clientX - bannerRect.left - dragOffset.current.x;
                let newY = e.clientY - bannerRect.top - dragOffset.current.y;

                // [NEW] Relaxed Constraints (Allow partial overflow)
                // Allow dragging slightly off-screen (-10% to 110% of banner size)
                const minX = -messageDims.current.w * 0.5;
                const maxX = bannerRect.width - messageDims.current.w * 0.5;
                const minY = -messageDims.current.h * 0.5;
                const maxY = bannerRect.height - messageDims.current.h * 0.5;

                newX = Math.max(minX, Math.min(maxX, newX));
                newY = Math.max(minY, Math.min(maxY, newY));

                // Convert to percentage for responsive scaling
                let percentX = (newX / bannerRect.width) * 100;
                let percentY = (newY / bannerRect.height) * 100;

                setBannerMessage(prev => ({ ...prev, x: percentX, y: percentY, isRightAligned: false }));
            });
        };

        const handleMouseUp = () => {
            setIsDraggingMessage(false);
            cancelAnimationFrame(requestRef.current); // Cleanup
        };

        if (isDraggingMessage) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        }
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            cancelAnimationFrame(requestRef.current); // Cleanup
        };
    }, [isDraggingMessage]);

    const handleSaveMessage = () => {
        setBannerMessage(prev => ({ ...prev, type: 'custom', text: messageForm.text, source: messageForm.source }));
        setIsMessageModalOpen(false);
    };

    const handleResetMessage = () => {
        setBannerMessage(prev => ({ ...prev, type: 'default' }));
        setIsMessageModalOpen(false);
    };

    const handleDismissRadar = (id, actionName) => {
        setRadarConfirmData({ id, actionName });
    };

    const confirmRadarDismiss = () => {
        if (!radarConfirmData) return;
        const { id } = radarConfirmData;
        const today = new Date().toLocaleDateString("en-CA");
        const newIds = [...dismissedRadarIds, id];
        setDismissedRadarIds(newIds);
        localStorage.setItem(`dashboard_radar_dismissed_${today}`, JSON.stringify(newIds));

        // [HAPPY MOMENT] Celebrate pastoral success
        triggerHappyMoment();

        openAlert("한 명의 영혼을 향한 사랑, 오늘도 참 귀합니다! ✨", "완료");
        setRadarConfirmData(null);
        setSelectedRecommendation(null);
    };
    const { handlers: longPressHandlers, isTriggered: isLongPressTriggered } = useLongPress(() => {
        if (currentBreakpoint === 'xs' || currentBreakpoint === 'xxs' || window.innerWidth < 768) return;
        setIsEditMode(true);
    }, 800);

    const handleWidgetClick = (e) => {
        if (isEditMode || isLongPressTriggered()) {
            e.stopPropagation();
        }
    };
    const [isScheduleEditOpen, setIsScheduleEditOpen] = useState(false);
    const [scheduleFormData, setScheduleFormData] = useState({ id: null, title: "", date: "", startTime: "", endTime: "", location: "", description: "", type: "worship" });

    // [MODIFIED] todos는 이제 Firestore 동기화 - 훅에서 초기값 가져오고 업데이트 시 동기화
    const [todos, setTodosLocal] = useState([]);
    const [newTodo, setNewTodo] = useState("");
    const [editingId, setEditingId] = useState(null);
    const [editText, setEditText] = useState("");
    const [showMobileSecondaryInsights, setShowMobileSecondaryInsights] = useState(false);

    // [NEW] 훅에서 todos 동기화
    useEffect(() => {
        if (dashboardPrefs.todos && dashboardPrefs.todos.length > 0) {
            setTodosLocal(dashboardPrefs.todos);
        }
    }, [dashboardPrefs.todos]);

    // [NEW] todos 업데이트 시 Firestore 동기화
    const setTodos = (newTodosOrFn) => {
        setTodosLocal(prev => {
            const newTodos = typeof newTodosOrFn === 'function' ? newTodosOrFn(prev) : newTodosOrFn;
            updateDashboardPrefs('todos', newTodos);
            return newTodos;
        });
    };

    // [메모장 상태 제거됨]

    useEffect(() => {
        const timer = setInterval(() => setCurrentDate(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        const handleEsc = (event) => {
            if (isAaronOpen) return; // [NEW] 아론이 열려있으면 무시 (App.js에서 처리)
            if (event.key === "Escape") {
                if (isScheduleEditOpen) setIsScheduleEditOpen(false);
                else if (selectedRecommendation) setSelectedRecommendation(null);
                else if (selectedSchedule) setSelectedSchedule(null);
                else if (isMessageModalOpen) setIsMessageModalOpen(false); // [NEW]
                else if (isEditMode) setIsEditMode(false); // [NEW] Exit Edit Mode

            }
        };
        window.addEventListener("keydown", handleEsc);
        return () => window.removeEventListener("keydown", handleEsc);
    }, [selectedSchedule, isScheduleEditOpen, selectedRecommendation, onToggleSettings, isAaronOpen, isEditMode, isMessageModalOpen, isSettingsOpen]);

    // [날씨 연동] Open-Meteo API
    const [weatherData, setWeatherData] = useState(null);

    useEffect(() => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;
                    fetch(`https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current_weather=true`)
                        .then(res => res.json())
                        .then(data => {
                            setWeatherData(data.current_weather);
                        })
                        .catch(err => console.error("Weather fetch error:", err));
                },
                (error) => {
                    console.error("Geolocation error:", error);
                }
            );
        }
    }, []);

    const getWeatherInfo = (code, isDay = 1) => {
        // WMO Weather interpretation codes (https://open-meteo.com/en/docs)
        // isDay: 1 = Day, 0 = Night
        const isNight = isDay === 0;

        if (code === 0) return {
            text: "맑음",
            icon: <img src={DASHBOARD_ICONS.weather_sun} alt="sunny" className="w-12 h-12 drop-shadow-md" />
        };
        if (code >= 1 && code <= 3) return {
            text: "구름 조금",
            icon: <img src={DASHBOARD_ICONS.weather_cloud_sun} alt="cloudy" className="w-12 h-12 drop-shadow-md" />
        };
        if (code >= 45 && code <= 48) return { text: "안개", icon: <img src={DASHBOARD_ICONS.weather_fog} alt="fog" className="w-12 h-12 drop-shadow-md" /> };
        if (code >= 51 && code <= 67) return { text: "비", icon: <img src={DASHBOARD_ICONS.weather_rain} alt="rain" className="w-12 h-12 drop-shadow-md" /> };
        if (code >= 71 && code <= 77) return { text: "눈", icon: <img src={DASHBOARD_ICONS.weather_snow} alt="snow" className="w-12 h-12 drop-shadow-md" /> };
        if (code >= 80 && code <= 82) return { text: "소나기", icon: <img src={DASHBOARD_ICONS.weather_rain} alt="shower" className="w-12 h-12 drop-shadow-md" /> };
        if (code >= 85 && code <= 86) return { text: "눈보라", icon: <img src={DASHBOARD_ICONS.weather_snow} alt="snow" className="w-12 h-12 drop-shadow-md" /> };
        if (code >= 95) return { text: "뇌우", icon: <img src={DASHBOARD_ICONS.weather_lightning} alt="thunder" className="w-12 h-12 drop-shadow-md" /> };
        return { text: "흐림", icon: <img src={DASHBOARD_ICONS.weather_cloud} alt="cloud" className="w-12 h-12 drop-shadow-md" /> };
    };

    // [Zero to One] 시간대별 하늘 테마 결정 로직
    const currentSkyTheme = useMemo(() => getCurrentSkyTheme(currentDate, DASHBOARD_SKY_THEMES), [currentDate]);

    const handleAddTodo = (e) => { if (e.key === 'Enter' && newTodo.trim() !== "") { setTodos([...todos, { id: Date.now(), text: newTodo, completed: false }]); setNewTodo(""); } };
    const toggleTodo = (id) => { if (editingId === id) return; setTodos(todos.map(todo => todo.id === id ? { ...todo, completed: !todo.completed } : todo)); };
    const deleteTodo = (id) => {
        openConfirm(
            "할 일 삭제",
            "이 할 일을 삭제하시겠습니까?",
            () => setTodos(todos.filter(todo => todo.id !== id))
        );
    };
    const startEdit = (todo) => { setEditingId(todo.id); setEditText(todo.text); };
    const saveEdit = () => { if (editText.trim() !== "") { setTodos(todos.map(todo => todo.id === editingId ? { ...todo, text: editText } : todo)); } setEditingId(null); };
    const cancelEdit = () => setEditingId(null);

    const handleCopySchedule = (s) => {
        const timeStr = s.startTime ? (s.endTime ? `${s.startTime} ~ ${s.endTime}` : s.startTime) : "시간 미정";
        const checklistStr = (s.checklists || []).map(c => `- [${c.checked ? 'v' : ' '}] ${c.text}`).join('\n');
        const text = `[일정 공유]\n제목: ${s.title}\n날짜: ${s.date}\n시간: ${timeStr}\n장소: ${s.location || "-"}\n내용: ${s.description || "-"}\n\n[체크리스트]\n${checklistStr}`;

        if (navigator.clipboard) {
            navigator.clipboard.writeText(text).then(() => openAlert("일정이 복사되었습니다.", "알림")).catch(() => openAlert("복사 실패", "오류"));
        } else {
            // Fallback
            const textarea = document.createElement("textarea");
            textarea.value = text;
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand("copy");
            document.body.removeChild(textarea);
            openAlert("일정이 복사되었습니다.", "알림");
        }
    };

    const openScheduleEdit = (s) => { setScheduleFormData({ ...s }); setIsScheduleEditOpen(true); setSelectedSchedule(null); };
    const handleScheduleSubmit = (e) => { e.preventDefault(); onUpdate(scheduleFormData); setIsScheduleEditOpen(false); };

    const dateTimeInfo = useMemo(() => {
        const datePart = currentDate.toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' });
        const ampm = currentDate.getHours() >= 12 ? "오후" : "오전";
        const timePart = currentDate.toLocaleTimeString('ko-KR', { hour: 'numeric', minute: '2-digit', hour12: true }).replace("오전", "").replace("오후", "").trim();

        let weatherIcon = <Sun size={24} className="text-yellow-300 drop-shadow-md" />;
        let weatherText = "날씨 정보 없음";

        if (weatherData) {
            const info = getWeatherInfo(weatherData.weathercode, weatherData.is_day);
            weatherIcon = info.icon;
            weatherText = `${info.text} ${Math.round(weatherData.temperature)}°C`;
        }

        return { date: datePart, time: timePart, ampm, weatherIcon, weatherText };
    }, [currentDate, weatherData]);

    const userName = userProfile?.name || "사역자";
    const churchName = userProfile?.churchName || "행복한교회";
    const deptName = selectedDepartment === 'All' ? '전체 부서' : (selectedDepartment || userProfile?.deptName || "교육부");

    const todayMobileLabel = useMemo(
        () => currentDate.toLocaleDateString("ko-KR", { month: "numeric", day: "numeric", weekday: "short" }),
        [currentDate]
    );
    const pendingTodoCount = useMemo(
        () => todos.filter((todo) => !todo.completed).length,
        [todos]
    );
    const pastoralNeedCount = useMemo(
        () => radarRecommendations.length,
        [radarRecommendations]
    );
    const longTermAbsenteeCount = useMemo(
        () => radarRecommendations.filter((item) => item?.id?.startsWith("absent")).length,
        [radarRecommendations]
    );

    const todoBoardRef = useRef(null);
    const radarBoardRef = useRef(null);

    const dailyVerse = useMemo(() => getDailyVerse(new Date()), []);

    // [NEW] 실제 표시할 메시지
    const displayMessage = bannerMessage.type === 'custom'
        ? { text: bannerMessage.text, ref: bannerMessage.source }
        : dailyVerse;

    const greetingInfo = useMemo(() => getGreetingInfo(currentDate, DASHBOARD_ICONS), [currentDate]);

    const handleCloseRecommendation = () => setSelectedRecommendation(null);
    const handleNavigateStudentsFromRecommendation = (recommendation) => {
        const deepLinkStudentId =
            recommendation?.studentId ||
            recommendation?.id?.split("-")?.slice(1)?.join("-") ||
            null;

        if (deepLinkStudentId && typeof onOpenStudentDetailFromRecommendation === "function") {
            onOpenStudentDetailFromRecommendation(deepLinkStudentId);
            setSelectedRecommendation(null);
            return;
        }

        onNavigate("students");
        setSelectedRecommendation(null);
    };
    const handleNavigateToAbsentees = () => {
        if (typeof onNavigateToStatisticsSection === "function") {
            onNavigateToStatisticsSection("absentees");
            return;
        }
        onNavigate("statistics");
    };
    const handleFocusTodoBoard = () => {
        todoBoardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    const handleFocusRadarBoard = () => {
        if (!visibleWidgets.radar) {
            onNavigate("statistics");
            return;
        }
        if (!showMobileSecondaryInsights) {
            setShowMobileSecondaryInsights(true);
            window.setTimeout(() => {
                radarBoardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 160);
            return;
        }
        radarBoardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    const handleConfirmRecommendation = () => {
        if (!selectedRecommendation) return;
        handleDismissRadar(selectedRecommendation.id, selectedRecommendation.action);
        setSelectedRecommendation(null);
    };

    // Force layout properties based on isEditMode to ensure consistency
    const processedLayouts = useMemo(() => {
        const newLayouts = { ...layouts };
        Object.keys(newLayouts).forEach(key => {
            newLayouts[key] = newLayouts[key].map(item => ({
                ...item,
                isResizable: isEditMode,
                isDraggable: isEditMode,
                // [Fix] Smart Locking: Lock banner if dragging other widgets
                static: item.i === 'banner' ? (item.static || isDragging) : item.static,
                maxH: undefined,
                maxW: undefined,
                minW: 1, // [Fix] Allow 1x1 resizing
                minH: 1  // [Fix] Allow 1x1 resizing
            }));
        });
        return newLayouts;
    }, [layouts, isEditMode, isDragging]);

    const renderMainWidget = (id) => {
        const h = getWidgetHeight(id);

        let content = null;
        if (id === 'schedule') {
            content = (
                <DashboardScheduleSection
                    GlassCard={DashboardGlassCard}
                    longPressHandlers={longPressHandlers}
                    handleWidgetClick={handleWidgetClick}
                    isMobile={isMobile}
                    isEditMode={isEditMode}
                    sectionHeight={h}
                    icons={DASHBOARD_ICONS}
                    onNavigate={onNavigate}
                    isLongPressTriggered={isLongPressTriggered}
                    filteredSchedules={filteredSchedules}
                    schedules={schedules}
                    setSelectedSchedule={setSelectedSchedule}
                    userProfile={userProfile}
                    getScheduleDDay={getScheduleDDay}
                    getScheduleTypeColor={getScheduleTypeColor}
                    getScheduleTypeName={getScheduleTypeName}
                />
            );
        }
        if (id === 'radar') {
            content = (
                <DashboardRecommendationSection
                    GlassCard={DashboardGlassCard}
                    longPressHandlers={longPressHandlers}
                    handleWidgetClick={handleWidgetClick}
                    isMobile={isMobile}
                    sectionHeight={h}
                    radarRecommendations={radarRecommendations}
                    isEditMode={isEditMode}
                    setSelectedRecommendation={setSelectedRecommendation}
                    handleDismissRadar={handleDismissRadar}
                    icons={DASHBOARD_ICONS}
                />
            );
        }
        if (id === 'goals') {
            content = (
                <DashboardActivitySection
                    GlassCard={DashboardGlassCard}
                    longPressHandlers={longPressHandlers}
                    handleWidgetClick={handleWidgetClick}
                    isEditMode={isEditMode}
                    icons={DASHBOARD_ICONS}
                    currentSlogan={currentSlogan}
                    handleGoalChange={handleGoalChange}
                    selectedDepartment={selectedDepartment}
                    theme={theme}
                    currentGoals={currentGoals}
                />
            );
        }
        return content;
    };

    const renderBanner = () => {
        return (
            <DashboardBannerSection
                bannerRef={bannerRef}
                longPressHandlers={longPressHandlers}
                isMobile={isMobile}
                isEditMode={isEditMode}
                currentSkyTheme={currentSkyTheme}
                isDraggingMessage={isDraggingMessage}
                bannerMessage={bannerMessage}
                handleMessageMouseDown={handleMessageMouseDown}
                setMessageForm={setMessageForm}
                displayMessage={displayMessage}
                setIsMessageModalOpen={setIsMessageModalOpen}
                dateTimeInfo={dateTimeInfo}
                churchName={churchName}
                deptName={deptName}
                userName={userName}
                greetingInfo={greetingInfo}
                deleteWidget={deleteWidget}
            />
        );
    };

    const renderKPICards = () => {
        return (
            <DashboardKpiSection
                visibleWidgets={visibleWidgets}
                isMobile={isMobile}
                isEditMode={isEditMode}
                longPressHandlers={longPressHandlers}
                DashboardCard={DashboardCard}
                totalStudents={totalStudents}
                avgAttendance={avgAttendance}
                birthdayCount={birthdayStudents.length}
                remainingBudget={remainingBudget}
                level={level}
                earnedBadgesCount={earnedBadges.length}
                icons={DASHBOARD_ICONS}
                attStatus={attStatus}
                birthStatus={birthStatus}
                budgetStatus={budgetStatus}
                onNavigate={onNavigate}
                onOpenProfile={onOpenProfile}
                isLongPressTriggered={isLongPressTriggered}
                deleteWidget={deleteWidget}
            />
        );
    };

    const renderTodo = () => {
        return (
            <DashboardTodoSection
                isEditMode={isEditMode}
                isMobile={isMobile}
                longPressHandlers={longPressHandlers}
                handleWidgetClick={handleWidgetClick}
                GlassCard={DashboardGlassCard}
                theme={theme}
                icons={DASHBOARD_ICONS}
                todos={todos}
                newTodo={newTodo}
                setNewTodo={setNewTodo}
                handleAddTodo={handleAddTodo}
                editingId={editingId}
                editText={editText}
                setEditText={setEditText}
                saveEdit={saveEdit}
                cancelEdit={cancelEdit}
                toggleTodo={toggleTodo}
                startEdit={startEdit}
                deleteTodo={deleteTodo}
                deleteWidget={deleteWidget}
            />
        );
    };

    return (
        <div className="w-full h-full relative" onClick={() => setIsEditMode(false)}>
            {/* Background Sky Theme (Only for Desktop now effectively, or handled in Banner) */}
            <div className="absolute inset-0 bg-[#F0F0EB] -z-20"></div>

            {/* Modals */}
            <DashboardScheduleEditModal
                isOpen={isScheduleEditOpen}
                onClose={() => setIsScheduleEditOpen(false)}
                scheduleFormData={scheduleFormData}
                setScheduleFormData={setScheduleFormData}
                onSubmit={handleScheduleSubmit}
            />
            <DashboardMessageModal
                isOpen={isMessageModalOpen}
                onClose={() => setIsMessageModalOpen(false)}
                messageForm={messageForm}
                setMessageForm={setMessageForm}
                onReset={handleResetMessage}
                onSave={handleSaveMessage}
            />
            {isSettingsOpen && (
                <DashboardSettingsModal
                    isOpen={isSettingsOpen}
                    onClose={onCloseSettings}
                    visibleWidgets={visibleWidgets}
                    onToggleWidget={toggleWidget}
                    onResetLayout={resetLayout}
                    isEditMode={isEditMode}
                    onToggleEditMode={() => setIsEditMode(!isEditMode)}
                    theme={theme}
                />
            )}
            <DashboardRecommendationModal
                selectedRecommendation={selectedRecommendation}
                onClose={handleCloseRecommendation}
                onNavigateStudents={handleNavigateStudentsFromRecommendation}
                onConfirm={handleConfirmRecommendation}
                icons={DASHBOARD_ICONS}
            />
            <DashboardRadarConfirmModal
                radarConfirmData={radarConfirmData}
                onClose={() => setRadarConfirmData(null)}
                onConfirm={confirmRadarDismiss}
                checkIcon={DASHBOARD_ICONS.check}
            />

            {isMobile ? (
                // --- MOBILE CUSTOM LAYOUT ---
                <div className="flex flex-col h-full overflow-y-auto pb-[calc(112px+env(safe-area-inset-bottom,0px))] px-4 pt-4 space-y-4 custom-scrollbar">
                    {/* 1. Department Goals Card (for all users) */}
                    <div>
                        <DashboardTeacherGoalsCard
                            theme={theme}
                            selectedDepartment={selectedDepartment}
                            userProfile={userProfile}
                            currentSlogan={currentSlogan}
                            currentGoals={currentGoals}
                            defaultCollapsed={true}
                        />
                    </div>

                    {/* 2. Immediate Actions */}
                    <div className="bg-white rounded-2xl border border-gray-100 p-3 shadow-sm">
                        <div className="flex items-center justify-between mb-2.5 px-0.5">
                            <h3 className="font-do-hyeon text-[15px] text-gray-800">오늘 바로 실행</h3>
                            <span className="text-[11px] text-gray-500 font-bold">{todayMobileLabel}</span>
                        </div>
                        <div className="grid grid-cols-3 gap-2 mb-2.5">
                            <div className="rounded-xl border border-gray-100 bg-gray-50/80 px-2 py-2">
                                <p className="text-[10px] text-gray-500 font-bold">현재 부서</p>
                                <p className="text-[11px] text-gray-800 font-black truncate">{deptName}</p>
                            </div>
                            <div className="rounded-xl border border-gray-100 bg-gray-50/80 px-2 py-2 text-right">
                                <p className="text-[10px] text-gray-500 font-bold">장기결석</p>
                                <p className="text-[12px] text-gray-800 font-black">{longTermAbsenteeCount}명</p>
                            </div>
                            <div className="rounded-xl border border-gray-100 bg-gray-50/80 px-2 py-2 text-right">
                                <p className="text-[10px] text-gray-500 font-bold">출석률</p>
                                <p className="text-[12px] text-gray-800 font-black">{avgAttendance}%</p>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <button
                                onClick={handleFocusTodoBoard}
                                className="w-full min-h-11 rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-left active:scale-[0.99] transition flex items-center justify-between gap-3"
                            >
                                <div className={`${theme.text}`}>
                                    <span className="text-[12px] font-black text-gray-800">할 일 정리</span>
                                </div>
                                <div className="flex items-center gap-1.5 text-[11px] font-bold text-gray-500">
                                    <span>미완료 {pendingTodoCount}개</span>
                                </div>
                            </button>
                            <button
                                onClick={handleNavigateToAbsentees}
                                className="w-full min-h-11 rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-left active:scale-[0.99] transition flex items-center justify-between gap-3"
                            >
                                <div className={`${theme.text}`}>
                                    <span className="text-[12px] font-black text-gray-800">장기결석 관리</span>
                                </div>
                                <div className="flex items-center gap-1.5 text-[11px] font-bold text-gray-500">
                                    <span>대상 {longTermAbsenteeCount}명</span>
                                </div>
                            </button>
                            {showMobileSecondaryInsights && (
                                <>
                                    <button
                                        onClick={handleFocusRadarBoard}
                                        className="w-full min-h-11 rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-left active:scale-[0.99] transition flex items-center justify-between gap-3"
                                    >
                                        <div className={`${theme.text}`}>
                                            <span className="text-[12px] font-black text-gray-800">돌봄 레이더</span>
                                        </div>
                                        <div className="flex items-center gap-1.5 text-[11px] font-bold text-gray-500">
                                            <span>추천 {pastoralNeedCount}건</span>
                                        </div>
                                    </button>
                                    <button
                                        onClick={() => onNavigate("statistics")}
                                        className="w-full min-h-11 rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-left active:scale-[0.99] transition flex items-center justify-between gap-3"
                                    >
                                        <div className={`${theme.text}`}>
                                            <span className="text-[12px] font-black text-gray-800">통계 자세히</span>
                                        </div>
                                        <div className="flex items-center gap-1.5 text-[11px] font-bold text-gray-500">
                                            <span>분석 보기</span>
                                        </div>
                                    </button>
                                </>
                            )}
                            <button
                                onClick={() => setShowMobileSecondaryInsights((prev) => !prev)}
                                className="w-full h-9 rounded-xl border border-gray-200 bg-gray-50 text-[11px] font-bold text-gray-600"
                            >
                                {showMobileSecondaryInsights ? "보조 실행 숨기기" : "보조 실행 보기"}
                            </button>
                        </div>
                    </div>

                    {/* 3. Core Boards */}
                    <div className="space-y-3">
                        <div className="flex items-center gap-2 px-1 mt-2 mb-1">
                            <div className={`w-1 h-4 rounded-full ${theme.bg}`}></div>
                            <h3 className="font-do-hyeon text-[15px] text-gray-700">핵심 보드</h3>
                        </div>
                        <div className="space-y-3">
                            {/* Todo */}
                            {visibleWidgets.todo && (
                                <div ref={todoBoardRef} className="h-[300px]">
                                    {renderTodo()}
                                </div>
                            )}
                            {!visibleWidgets.todo && visibleWidgets.schedule && (
                                <div className="h-[300px]">
                                    {renderMainWidget('schedule')}
                                </div>
                            )}
                            {/* Secondary insights */}
                            {showMobileSecondaryInsights && visibleWidgets.schedule && visibleWidgets.todo && (
                                <div className="h-[300px]">
                                    {renderMainWidget('schedule')}
                                </div>
                            )}
                            {showMobileSecondaryInsights && visibleWidgets.radar && (
                                <div ref={radarBoardRef} className="h-[300px]">
                                    {renderMainWidget('radar')}
                                </div>
                            )}
                            {showMobileSecondaryInsights && visibleWidgets.goals && userProfile?.memberRole !== 'teacher' && (
                                <div className="h-[300px]">
                                    {renderMainWidget('goals')}
                                </div>
                            )}
                        </div>
                    </div>
                    {/* Legacy horizontal board intentionally removed for compact-first mobile IA */}
                </div>
            ) : (
                // --- DESKTOP GRID LAYOUT ---
                <div className="w-full space-y-8 pb-20 pt-0 relative px-4 md:px-12 max-w-[1600px] mx-auto" onClick={() => isEditMode && setIsEditMode(false)}>
                    {isEditMode && (
                        <div className="fixed top-24 left-1/2 transform -translate-x-1/2 z-50 bg-black/70 text-white px-6 py-2 rounded-full shadow-lg backdrop-blur-md flex items-center gap-2">
                            <ArrowDownRight size={18} />
                            <span className="font-bold">위젯의 오른쪽 하단을 드래그하여 크기를 조절하세요</span>
                        </div>
                    )}

                    <ResponsiveGridLayout
                        className="layout mx-auto"
                        layouts={processedLayouts}
                        breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                        cols={{ lg: 15, md: 15, sm: 15, xs: 4, xxs: 4 }}
                        rowHeight={10}
                        isDraggable={isEditMode}
                        isResizable={isEditMode}
                        onBreakpointChange={onBreakpointChange}
                        resizeHandles={['se']}
                        draggableCancel=".non-draggable"
                        margin={
                            (currentBreakpoint === 'xs' || currentBreakpoint === 'xxs') ? [6, 6] :
                                currentBreakpoint === 'sm' ? [16, 16] : [32, 24]
                        }
                        containerPadding={
                            (currentBreakpoint === 'xs' || currentBreakpoint === 'xxs') ? [0, 0] : [0, 0]
                        }
                        compactType="vertical"
                        preventCollision={false}
                        onLayoutChange={onLayoutChange}
                        onDragStart={(layout, oldItem, newItem, placeholder, e, element) => {
                            dragRef.current = { i: newItem.i, x: newItem.x, y: newItem.y, w: newItem.w, h: newItem.h };
                            if (newItem.i !== 'banner') setIsDragging(true);
                        }}
                        onDragStop={(layout, oldItem, newItem, placeholder, e, element) => {
                            dragRef.current = null;
                            setIsDragging(false);
                        }}
                    >
                        {/* Banner Widget */}
                        <div key="banner">
                            {renderBanner()}
                        </div>

                        {/* KPI Cards (Array) */}
                        {renderKPICards()}

                        {/* Todo Widget */}
                        {visibleWidgets.todo && (
                            <div key="todo">
                                {renderTodo()}
                            </div>
                        )}

                        {/* Main Widgets */}
                        {['schedule', 'radar', 'goals'].filter(id => visibleWidgets[id] && !(id === 'goals' && (currentBreakpoint === 'xs' || currentBreakpoint === 'xxs'))).map(id => {
                            const content = renderMainWidget(id);
                            // We need the wrapper here for RGL.
                            return (
                                <div key={id}>
                                    <div className={`h-full ${isEditMode ? 'animate-shake ring-4 ring-blue-400/50 rounded-[2.5rem] relative z-40' : ''}`} style={{ height: '100%', animationDelay: isEditMode ? `${Math.random() * -0.5}s` : '0s' }}>
                                        {content}
                                        {isEditMode && (
                                            <>
                                                <button onMouseDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); deleteWidget(id); }} className="non-draggable absolute -top-2 -right-2 bg-red-500 text-white p-1.5 rounded-full shadow-lg hover:scale-110 transition z-50 cursor-pointer">
                                                    <X size={16} />
                                                </button>
                                                <div
                                                    className="react-resizable-handle react-resizable-handle-se absolute bottom-8 right-2 w-8 h-8 bg-white/30 backdrop-blur-md border border-white/60 text-[#2C3E30] rounded-full shadow-lg flex items-center justify-center cursor-se-resize z-50 hover:scale-110 hover:bg-white/50 transition-all"
                                                    style={{ touchAction: 'none' }}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <ArrowDownRight size={18} strokeWidth={3} />
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </ResponsiveGridLayout>
                </div>
            )}


            {/* 일정 상세 팝업 (Matched with ScheduleView) */}
            {/* 일정 상세 팝업 (Matched with ScheduleView) */}
            <ScheduleDetailModal
                schedule={selectedSchedule}
                onClose={() => setSelectedSchedule(null)}
                onEdit={openScheduleEdit}
                onDelete={(id) => {
                    openConfirm("일정 삭제", "정말로 이 일정을 삭제하시겠습니까?", () => { onDelete(id); setSelectedSchedule(null); });
                }}
                onShare={handleCopySchedule}
                onChecklistToggle={handleDetailChecklistToggle}
                theme={theme}
                userProfile={userProfile}
            />
        </div >
    );
}
